import React, { useState, useEffect, useCallback } from 'react';
import { useNavStep } from '../../lib/NavHistory';
import {
  Lock, ShieldCheck, ShieldAlert, ArrowLeft, Plus, Check, X,
  AlertTriangle, Clock, Microscope, Stethoscope, Archive, FileText,
  Send, Edit2, Save,
} from 'lucide-react';
import { canUse, canView } from '../../lib/aiPermissions';

/* ══════════════════════════════════════════════════════════════════════════
   ProprietaryVaultView — Phase F.3.4

   Locked-down vault of ZignaGenix proprietary medical protocols.
   2-step validation workflow: doctor + researcher both sign off, then admin
   publishes. State machine lives in routes/crm.js handlers and the DB CHECK
   constraint.

   Roles:
   - research, physician, admin, sysadmin → view + submit drafts
   - physician, admin, sysadmin → doctor validation
   - research, admin, sysadmin → researcher validation
   - admin, sysadmin → publish, retire
   ══════════════════════════════════════════════════════════════════════════ */

const STATUSES = [
  { id: 'all',                    label: 'All',              color: '#64748B' },
  { id: 'draft',                  label: 'Draft',            color: '#94A3B8' },
  { id: 'doctor_reviewed',        label: 'Doctor reviewed',  color: '#2563EB' },
  { id: 'researcher_reviewed',    label: 'Researcher reviewed', color: '#7C3AED' },
  { id: 'published_proprietary',  label: 'Published',        color: '#15803D' },
  { id: 'rejected',               label: 'Rejected',         color: '#B91C1C' },
  { id: 'retired',                label: 'Retired',          color: '#B45309' },
];

const STATUS_META = Object.fromEntries(STATUSES.map(s => [s.id, s]));

const IP_CLASSIFICATIONS = ['internal_use', 'trade_secret', 'patent_pending', 'patented', 'published_open'];

function statusBadge(status) {
  const meta = STATUS_META[status] || { label: status, color: '#64748B' };
  return (
    <span style={{
      display: 'inline-block', fontSize: 9, padding: '2px 7px',
      borderRadius: 4, background: meta.color + '20', color: meta.color,
      fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
    }}>{meta.label}</span>
  );
}

export default function ProprietaryVaultView() {
  const [resolvedRole, setResolvedRole] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await window.zignagenixAPI.getOwnProfile();
        if (!cancelled) setResolvedRole(me?.role || 'unknown');
      } catch { if (!cancelled) setResolvedRole('unknown'); }
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const filters = statusFilter === 'all' ? {} : { status: statusFilter };
      const r = await window.zignagenixAPI.listVaultEntries(filters);
      setEntries(Array.isArray(r) ? r : []);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { if (resolvedRole) reload(); }, [reload, resolvedRole]);

  // #65 Phase C2b-2: the vault entry detail (read-only) is this page's master->
  // detail sub-view. Register ONE Back step so global Back returns to the vault
  // LIST before navigating away — same handler the in-view "Back" button uses.
  // MUST be unconditional and ABOVE the `resolvedRole === null` / `!canView` /
  // `selectedId !== null` early returns below (rules-of-hooks). Label is a LITERAL
  // static string — never a protocol name, entry id, or row value (sessionStorage-
  // backed nav stack). validate/reject/retire inputs live in transient modals, not
  // the detail body, so no unsaved-input guard is needed here.
  useNavStep({
    active: selectedId !== null,
    label: 'Vault record',
    onBack: () => { setSelectedId(null); reload(); },
  });

  if (resolvedRole === null) {
    return <div style={{ padding: 24, color: '#94A3B8', fontSize: 13 }}>Loading…</div>;
  }

  if (!canView(resolvedRole, 'proprietary_vault_view')) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <ShieldAlert size={32} color="#94A3B8" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
          Proprietary Vault is restricted
        </div>
        <div style={{ fontSize: 12, color: '#94A3B8', maxWidth: 420, margin: '0 auto' }}>
          Only research, physician, admin, and sysadmin roles can access proprietary protocols.
        </div>
      </div>
    );
  }

  if (selectedId !== null) {
    return (
      <VaultDetailView
        entryId={selectedId}
        role={resolvedRole}
        onBack={() => { setSelectedId(null); reload(); }}
      />
    );
  }

  const canSubmit = canUse(resolvedRole, 'proprietary_vault_submit');

  return (
    <div style={{ padding: 16 }}>
      <div style={{
        background: 'linear-gradient(135deg, #FEF2F2 0%, #FAF5FF 100%)',
        border: '1px solid #FECACA', borderRadius: 10, padding: 14, marginBottom: 16,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <Lock size={18} color="#B91C1C" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: '#7F1D1D', lineHeight: 1.5 }}>
          <strong>Proprietary Protocols Vault — restricted, encrypted at rest.</strong> ZignaGenix-proprietary
          medical protocols pending or post 2-step validation. Each entry requires both a doctor and a
          researcher sign-off before an admin can publish. Researchers see a de-identified view; doctors
          and admins see full content. Audit log captures every state change.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Lock size={18} color="#7C3AED" />
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' }}>
            Proprietary Vault
          </h2>
        </div>
        {canSubmit && (
          <button onClick={() => setShowCreate(true)} style={{
            padding: '7px 14px', borderRadius: 8, border: 'none',
            background: 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)',
            color: '#FFF', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Plus size={13} /> Submit Draft
          </button>
        )}
      </div>

      {/* Status filter chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {STATUSES.map(s => {
          const sel = statusFilter === s.id;
          return (
            <button key={s.id} onClick={() => setStatusFilter(s.id)} style={{
              padding: '4px 12px', borderRadius: 16,
              border: sel ? `1px solid ${s.color}` : '1px solid #CBD5E1',
              background: sel ? s.color + '15' : '#F8FAFC',
              color: sel ? s.color : '#64748B',
              fontWeight: sel ? 700 : 500, fontSize: 11, cursor: 'pointer',
            }}>
              {s.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ fontSize: 12, color: '#94A3B8' }}>Loading vault entries…</div>
      ) : entries.length === 0 ? (
        <div style={{
          background: '#F8FAFC', border: '1px dashed #CBD5E1',
          borderRadius: 12, padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
            No vault entries{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.
          </div>
          {canSubmit && statusFilter === 'all' && (
            <div style={{ fontSize: 11, color: '#94A3B8', maxWidth: 480, margin: '0 auto' }}>
              Submit a draft protocol from a research synthesis artifact or compose one from scratch.
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.map(e => (
            <VaultListRow key={e.id} entry={e} onOpen={() => setSelectedId(e.id)} />
          ))}
        </div>
      )}

      {showCreate && (
        <SubmitDraftModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); reload(); setSelectedId(id); }}
        />
      )}
    </div>
  );
}

function VaultListRow({ entry, onOpen }) {
  const bothValidated = entry.doctor_validated_at && entry.researcher_validated_at;
  return (
    <button onClick={onOpen} style={{
      textAlign: 'left', background: '#FFFFFF', border: '1px solid #E2E8F0',
      borderRadius: 10, padding: 14, cursor: 'pointer',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Lock size={14} color="#7C3AED" />
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', flex: 1 }}>
          {entry.protocol_name} <span style={{ color: '#94A3B8', fontSize: 11 }}>{entry.protocol_version}</span>
        </span>
        {statusBadge(entry.status)}
      </div>
      <div style={{ fontSize: 11, color: '#475569' }}>{entry.target_indication}</div>
      <div style={{ display: 'flex', gap: 12, fontSize: 10, color: '#94A3B8', flexWrap: 'wrap' }}>
        <span>by {entry.creator_name}</span>
        <span>·</span>
        <span>{new Date(entry.created_at).toLocaleDateString()}</span>
        {entry.intervention_type && <><span>·</span><span>{entry.intervention_type}</span></>}
        {entry.ip_classification && <><span>·</span><span>{entry.ip_classification.replace(/_/g, ' ')}</span></>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
        <ValidationDot label="Doctor" done={!!entry.doctor_validated_at} who={entry.doctor_validator_name} when={entry.doctor_validated_at} />
        <ValidationDot label="Researcher" done={!!entry.researcher_validated_at} who={entry.researcher_validator_name} when={entry.researcher_validated_at} />
        {bothValidated && entry.status !== 'published_proprietary' && (
          <span style={{ fontSize: 10, color: '#15803D', fontWeight: 700,
            background: '#DCFCE7', padding: '2px 8px', borderRadius: 4 }}>
            ✓ Ready to publish
          </span>
        )}
      </div>
    </button>
  );
}

function ValidationDot({ label, done, who, when }) {
  return (
    <span style={{ fontSize: 10, color: done ? '#15803D' : '#94A3B8',
      background: done ? '#DCFCE7' : '#F1F5F9', padding: '2px 8px', borderRadius: 4,
      fontWeight: done ? 700 : 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {done ? <Check size={9} /> : <Clock size={9} />}
      {label}{done && who ? `: ${who.split(' ')[0]}` : ''}
    </span>
  );
}

/* ─── Detail view ──────────────────────────────────────────────────────── */

function VaultDetailView({ entryId, role, onBack }) {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showValidate, setShowValidate] = useState(null); // 'doctor' | 'researcher'
  const [showReject, setShowReject] = useState(false);
  const [showRetire, setShowRetire] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await window.zignagenixAPI.getVaultEntry(entryId);
      if (r?.error === 'not_found') setError('Entry not found');
      else setEntry(r);
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setLoading(false); }
  }, [entryId]);

  useEffect(() => { reload(); }, [reload]);

  const isFullAccess = ['physician', 'admin', 'sysadmin'].includes(role);
  const canDoctor   = canUse(role, 'proprietary_vault_validate_doctor');
  const canResearch = canUse(role, 'proprietary_vault_validate_research');
  const canPublish  = canUse(role, 'proprietary_vault_publish');

  if (loading) return <div style={{ padding: 24, fontSize: 12, color: '#94A3B8' }}>Loading…</div>;
  if (error) return (
    <div style={{ padding: 24 }}>
      <button onClick={onBack} style={btnGhost}><ArrowLeft size={13} /> Back</button>
      <div style={{ marginTop: 16, color: '#B91C1C', fontSize: 13 }}>{error}</div>
    </div>
  );
  if (!entry) return null;

  const bothValidated = entry.doctor_validated_at && entry.researcher_validated_at;
  const isPublished = entry.status === 'published_proprietary';

  const doPublish = async () => {
    if (!window.confirm(`Publish "${entry.protocol_name} ${entry.protocol_version}" to the proprietary library? This makes it active for clinical decision support.`)) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.vaultPublish({ id: entry.id });
      if (r?.success) reload();
      else alert(`Publish failed: ${r?.error || 'unknown'}`);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button onClick={onBack} style={btnGhost}><ArrowLeft size={13} /> Back</button>
        <Lock size={18} color="#7C3AED" />
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#0F172A' }}>
          {entry.protocol_name} <span style={{ color: '#94A3B8', fontSize: 13, fontWeight: 500 }}>{entry.protocol_version}</span>
        </h2>
        {statusBadge(entry.status)}
      </div>

      {/* Two-step validation summary */}
      <div style={{
        background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 10,
        padding: 14, marginBottom: 14, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12,
      }}>
        <ValidationBlock
          icon={<Stethoscope size={14} color="#2563EB" />}
          label="Doctor validation"
          done={!!entry.doctor_validated_at}
          who={entry.doctor_validator_name}
          when={entry.doctor_validated_at}
          notes={isFullAccess ? entry.doctor_validation_notes : null}
        />
        <ValidationBlock
          icon={<Microscope size={14} color="#7C3AED" />}
          label="Researcher validation"
          done={!!entry.researcher_validated_at}
          who={entry.researcher_validator_name}
          when={entry.researcher_validated_at}
          notes={entry.researcher_validation_notes}
        />
        <div>
          <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, marginBottom: 4 }}>Publish gate</div>
          <div style={{ fontSize: 12 }}>
            {isPublished ? (
              <span style={{ color: '#15803D', fontWeight: 700 }}>
                ✓ Published {new Date(entry.published_at).toLocaleDateString()}
              </span>
            ) : bothValidated ? (
              <span style={{ color: '#15803D', fontWeight: 700 }}>Ready — awaiting admin</span>
            ) : (
              <span style={{ color: '#94A3B8' }}>Waiting on validations</span>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {canDoctor && !entry.doctor_validated_at && !['rejected', 'retired'].includes(entry.status) && (
          <button onClick={() => setShowValidate('doctor')} disabled={busy} style={btnPrimary('#2563EB')}>
            <Stethoscope size={13} /> Sign off as Doctor
          </button>
        )}
        {canResearch && !entry.researcher_validated_at && !['rejected', 'retired'].includes(entry.status) && (
          <button onClick={() => setShowValidate('researcher')} disabled={busy} style={btnPrimary('#7C3AED')}>
            <Microscope size={13} /> Sign off as Researcher
          </button>
        )}
        {canPublish && bothValidated && !isPublished && entry.status !== 'rejected' && entry.status !== 'retired' && (
          <button onClick={doPublish} disabled={busy} style={btnPrimary('#15803D')}>
            <Send size={13} /> Publish to library
          </button>
        )}
        {(canDoctor || canResearch) && !['published_proprietary', 'rejected', 'retired'].includes(entry.status) && (
          <button onClick={() => setShowReject(true)} disabled={busy} style={btnGhost}>
            <X size={13} /> Reject
          </button>
        )}
        {canPublish && isPublished && (
          <button onClick={() => setShowRetire(true)} disabled={busy} style={btnGhost}>
            <Archive size={13} /> Retire
          </button>
        )}
      </div>

      {/* Content */}
      <Field label="Target indication" value={entry.target_indication} />
      <Field label="Patient profile" value={entry.patient_profile} />
      <Field label="Intervention type" value={entry.intervention_type} />
      <Field label="IP classification" value={entry.ip_classification} />
      <FieldBlock label="Protocol summary" value={entry.protocol_summary} />
      {isFullAccess && (
        <>
          <FieldBlock label="Manufacturing protocol" value={entry.manufacturing_protocol} />
          <FieldBlock label="Bioengineering parameters" value={entry.bioengineering_parameters} />
          <FieldBlock label="Dosage regimen" value={entry.dosage_regimen} />
          <FieldBlock label="Patient selection" value={entry.patient_selection} />
          <FieldBlock label="Contraindications" value={entry.contraindications} />
        </>
      )}
      <FieldBlock label="Risk profile" value={entry.risk_profile} />
      <FieldBlock label="Expected outcomes" value={entry.expected_outcomes} />
      <FieldBlock label="Evidence basis" value={entry.evidence_basis} />

      {!isFullAccess && (
        <div style={{
          marginTop: 12, padding: 10, background: '#F1F5F9', border: '1px solid #CBD5E1',
          borderRadius: 8, fontSize: 11, color: '#475569',
        }}>
          <ShieldAlert size={12} style={{ display: 'inline', marginRight: 4 }} />
          Manufacturing, dosage, patient selection, and contraindications are hidden from this view.
          Doctors and admins see full content for clinical safety review.
        </div>
      )}

      {showValidate && (
        <ValidateModal
          mode={showValidate}
          entry={entry}
          onClose={() => setShowValidate(null)}
          onValidated={() => { setShowValidate(null); reload(); }}
        />
      )}
      {showReject && (
        <RejectModal
          entry={entry}
          onClose={() => setShowReject(false)}
          onRejected={() => { setShowReject(false); reload(); }}
        />
      )}
      {showRetire && (
        <RetireModal
          entry={entry}
          onClose={() => setShowRetire(false)}
          onRetired={() => { setShowRetire(false); reload(); }}
        />
      )}
    </div>
  );
}

function ValidationBlock({ icon, label, done, who, when, notes }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#475569',
        fontWeight: 700, marginBottom: 4 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 12 }}>
        {done ? (
          <>
            <span style={{ color: '#15803D', fontWeight: 700 }}>
              <Check size={11} style={{ verticalAlign: -2, marginRight: 3 }} />
              {who}
            </span>
            <div style={{ fontSize: 10, color: '#94A3B8' }}>
              {new Date(when).toLocaleString()}
            </div>
            {notes && (
              <div style={{ marginTop: 4, padding: 6, background: '#F8FAFC',
                borderRadius: 4, fontSize: 10, color: '#475569', whiteSpace: 'pre-wrap',
                maxHeight: 80, overflow: 'auto' }}>
                {notes}
              </div>
            )}
          </>
        ) : (
          <span style={{ color: '#94A3B8' }}>Pending</span>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', minWidth: 130 }}>{label}:</span>
      <span style={{ fontSize: 12, color: '#0F172A' }}>{value}</span>
    </div>
  );
}

function FieldBlock({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#0F172A', whiteSpace: 'pre-wrap',
        background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 6, padding: 10 }}>
        {value}
      </div>
    </div>
  );
}

/* ─── Validate / Reject / Retire modals ────────────────────────────────── */

function ValidateModal({ mode, entry, onClose, onValidated }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const isDoctor = mode === 'doctor';

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const fn = isDoctor ? window.zignagenixAPI.vaultValidateDoctor : window.zignagenixAPI.vaultValidateResearcher;
      const r = await fn({ id: entry.id, notes });
      if (r?.success) onValidated();
      else setError(r?.error || 'failed');
    } catch (e) { setError(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <ModalHeader title={`${isDoctor ? 'Doctor' : 'Researcher'} sign-off`}
        icon={isDoctor ? Stethoscope : Microscope}
        color={isDoctor ? '#2563EB' : '#7C3AED'} onClose={onClose} />
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 12, lineHeight: 1.5 }}>
          You are validating <strong>{entry.protocol_name} {entry.protocol_version}</strong> as
          {isDoctor ? ' a doctor (clinical safety + medical soundness).' : ' a researcher (scientific rigor + evidence basis).'}
          This action is logged and cannot be undone (only reversed by rejection).
        </div>
        <textarea
          value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Validation notes (optional but recommended). Document what you checked."
          rows={6}
          style={{ width: '100%', padding: 8, fontSize: 12, borderRadius: 6,
            border: '1px solid #CBD5E1', fontFamily: 'inherit' }}
        />
        {error && <div style={{ marginTop: 8, fontSize: 12, color: '#B91C1C' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} disabled={busy} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={busy} style={btnPrimary(isDoctor ? '#2563EB' : '#7C3AED')}>
            <ShieldCheck size={13} /> {busy ? 'Submitting…' : `Confirm ${isDoctor ? 'doctor' : 'researcher'} sign-off`}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

function RejectModal({ entry, onClose, onRejected }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!reason.trim()) { setError('reason required'); return; }
    setBusy(true); setError(null);
    try {
      const r = await window.zignagenixAPI.vaultReject({ id: entry.id, reason });
      if (r?.success) onRejected();
      else setError(r?.error || 'failed');
    } catch (e) { setError(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <ModalHeader title="Reject protocol" icon={X} color="#B91C1C" onClose={onClose} />
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 12, lineHeight: 1.5 }}>
          This will mark <strong>{entry.protocol_name} {entry.protocol_version}</strong> as rejected.
          The rejection reason is logged and visible to the creator.
        </div>
        <textarea
          value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Reason for rejection (required)" rows={5}
          style={{ width: '100%', padding: 8, fontSize: 12, borderRadius: 6,
            border: '1px solid #CBD5E1', fontFamily: 'inherit' }}
        />
        {error && <div style={{ marginTop: 8, fontSize: 12, color: '#B91C1C' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} disabled={busy} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={busy} style={btnPrimary('#B91C1C')}>
            <X size={13} /> {busy ? 'Rejecting…' : 'Confirm rejection'}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

function RetireModal({ entry, onClose, onRetired }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!reason.trim()) { setError('reason required'); return; }
    setBusy(true); setError(null);
    try {
      const r = await window.zignagenixAPI.vaultRetire({ id: entry.id, reason });
      if (r?.success) onRetired();
      else setError(r?.error || 'failed');
    } catch (e) { setError(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <ModalHeader title="Retire published protocol" icon={Archive} color="#B45309" onClose={onClose} />
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 12, lineHeight: 1.5 }}>
          Retire <strong>{entry.protocol_name} {entry.protocol_version}</strong>. It will no longer be
          available for new clinical decision support but historical references remain in the audit log.
        </div>
        <textarea
          value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Reason for retirement (superseded by version X, safety signal, etc.)" rows={5}
          style={{ width: '100%', padding: 8, fontSize: 12, borderRadius: 6,
            border: '1px solid #CBD5E1', fontFamily: 'inherit' }}
        />
        {error && <div style={{ marginTop: 8, fontSize: 12, color: '#B91C1C' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} disabled={busy} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={busy} style={btnPrimary('#B45309')}>
            <Archive size={13} /> {busy ? 'Retiring…' : 'Confirm retirement'}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

/* ─── Submit draft modal ───────────────────────────────────────────────── */

function SubmitDraftModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    protocol_name: '', protocol_version: 'v1.0', target_indication: '',
    patient_profile: '', intervention_type: '', ip_classification: 'internal_use',
    protocol_summary: '', manufacturing_protocol: '', bioengineering_parameters: '',
    dosage_regimen: '', patient_selection: '', contraindications: '',
    risk_profile: '', expected_outcomes: '', evidence_basis: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.protocol_name || !f.protocol_version || !f.target_indication) {
      setError('protocol_name, protocol_version, target_indication required');
      return;
    }
    setBusy(true); setError(null);
    try {
      const r = await window.zignagenixAPI.submitVaultDraft(f);
      if (r?.success) onCreated(r.id);
      else setError(r?.error || 'failed');
    } catch (e) { setError(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose} wide>
      <ModalHeader title="Submit proprietary protocol draft" icon={FileText} color="#7C3AED" onClose={onClose} />
      <div style={{ padding: 16, maxHeight: '70vh', overflowY: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
          <Inp label="Protocol name *" v={f.protocol_name} on={v => set('protocol_name', v)} />
          <Inp label="Version *" v={f.protocol_version} on={v => set('protocol_version', v)} />
          <Sel label="IP classification" v={f.ip_classification} on={v => set('ip_classification', v)}
            opts={IP_CLASSIFICATIONS} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 12 }}>
          <Inp label="Target indication *" v={f.target_indication} on={v => set('target_indication', v)} />
          <Inp label="Intervention type" v={f.intervention_type} on={v => set('intervention_type', v)}
            placeholder="stem_cell, nk_cell, exosome…" />
        </div>
        <Inp label="Patient profile (de-identified)" v={f.patient_profile} on={v => set('patient_profile', v)} />

        <TextArea label="Protocol summary"           v={f.protocol_summary}           on={v => set('protocol_summary', v)} />
        <TextArea label="Manufacturing protocol"     v={f.manufacturing_protocol}     on={v => set('manufacturing_protocol', v)} />
        <TextArea label="Bioengineering parameters"  v={f.bioengineering_parameters}  on={v => set('bioengineering_parameters', v)} />
        <TextArea label="Dosage regimen"             v={f.dosage_regimen}             on={v => set('dosage_regimen', v)} />
        <TextArea label="Patient selection"          v={f.patient_selection}          on={v => set('patient_selection', v)} />
        <TextArea label="Contraindications"          v={f.contraindications}          on={v => set('contraindications', v)} />
        <TextArea label="Risk profile"               v={f.risk_profile}               on={v => set('risk_profile', v)} />
        <TextArea label="Expected outcomes"          v={f.expected_outcomes}          on={v => set('expected_outcomes', v)} />
        <TextArea label="Evidence basis (citations)" v={f.evidence_basis}             on={v => set('evidence_basis', v)} />

        {error && <div style={{ marginTop: 8, fontSize: 12, color: '#B91C1C' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} disabled={busy} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={busy} style={btnPrimary('#7C3AED')}>
            <Save size={13} /> {busy ? 'Submitting…' : 'Submit as draft'}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

function Inp({ label, v, on, placeholder }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: '#475569', fontWeight: 700, marginBottom: 3 }}>{label}</label>
      <input type="text" value={v} onChange={e => on(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '7px 9px', fontSize: 12, borderRadius: 6, border: '1px solid #CBD5E1' }} />
    </div>
  );
}

function Sel({ label, v, on, opts }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: '#475569', fontWeight: 700, marginBottom: 3 }}>{label}</label>
      <select value={v} onChange={e => on(e.target.value)}
        style={{ width: '100%', padding: '7px 9px', fontSize: 12, borderRadius: 6, border: '1px solid #CBD5E1' }}>
        {opts.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
    </div>
  );
}

function TextArea({ label, v, on }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, color: '#475569', fontWeight: 700, marginBottom: 3 }}>{label}</label>
      <textarea value={v} onChange={e => on(e.target.value)} rows={3}
        style={{ width: '100%', padding: '7px 9px', fontSize: 12, borderRadius: 6,
          border: '1px solid #CBD5E1', fontFamily: 'inherit', resize: 'vertical' }} />
    </div>
  );
}

function Overlay({ children, onClose, wide }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#FFFFFF', borderRadius: 12, width: wide ? '90%' : '500px',
        maxWidth: wide ? 900 : 500, maxHeight: '90vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>{children}</div>
    </div>
  );
}

function ModalHeader({ title, icon: Icon, color, onClose }) {
  return (
    <div style={{
      padding: '12px 16px', borderBottom: '1px solid #E2E8F0',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      {Icon && <Icon size={16} color={color} />}
      <span style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', flex: 1 }}>{title}</span>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8',
        padding: 4, display: 'flex',
      }}><X size={16} /></button>
    </div>
  );
}

const btnGhost = {
  padding: '6px 12px', borderRadius: 6, border: '1px solid #CBD5E1',
  background: '#FFFFFF', color: '#475569', fontSize: 12, fontWeight: 600,
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
};
function btnPrimary(color) {
  return {
    padding: '6px 14px', borderRadius: 6, border: 'none', background: color,
    color: '#FFFFFF', fontSize: 12, fontWeight: 700,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
  };
}
